import { BrowserSupport } from '@tableau/browser-support';
import { createReportEventUnauthenticatedParams, ReportEventUnauthenticatedParams ,TelemetryForwarder, ValidPropertyValueType } from '@tableau/typescript-telemetry-forwarder';
import * as _ from 'underscore';
import * as uuidv4 from 'uuid/v4';
import { defaultMemoize } from 'reselect';

import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { SessionService } from '../../../src/commonInfra/services/SessionService';
import { ServiceLocator } from '../../app/ServiceLocator';

export type BaseTelemetryEvent = { type: string };
export type AnalyticsEventProperties = { [K: string]: ValidPropertyValueType };

/**
 * Only specific types (validPropertyValueType) are allowed as values for events.
 * Custom telemetry event interfaces can extend this type to ensure that type-safety.
 */
type ValidTelemetryEventInternal<T extends BaseTelemetryEvent, U extends keyof T> = {
  [K in U]: T[K] extends ValidPropertyValueType ? T[K] : never
};
export type ValidTelemetryEvent<T extends BaseTelemetryEvent> = ValidTelemetryEventInternal<T, keyof T>;

/**
 * TelemetryService handles sending telemetry events to the Google Big Query database.
 *
 * App-wide event properties get set through the reporter and eventFactory
 * Usage examples: UrlTelemetryService.ts for Angular and SiteSpace.tsx for React
 *
 * For setting up events in Google BigQuery, see https://mytableau.tableaucorp.com/x/0hOHBw
 */

function sendEvent<T extends BaseTelemetryEvent>(event: ValidTelemetryEvent<T>): void {
  const { type, ...customProps } = event as BaseTelemetryEvent & AnalyticsEventProperties;

  const properties: AnalyticsEventProperties = {
    ...customProps,
    ...getCommonProperties()
  };

  Object.keys(properties).forEach(key => {
    // since remove waitingForSession or getSessionInfo before we send telemetry, values of sessionInfo could be null
    // vizportal java code does not allow null value, so need convert to empty string
    if (properties[key] === undefined || properties[key] === null) {
      properties[key] = '';
    }
  });

  // java-event-client use NetworkUtilities.getHostName() to get hostName, so we do not need to add hostName
  // https://gitlab.tableausoftware.com/Product-Analytics/java-event-client/blob/master/src/main/java/com/tableau/telemetry/event/AEvent.java#L27
  const params: ReportEventUnauthenticatedParams = createReportEventUnauthenticatedParams(type, 'vizportalclient', properties);
  getTelemetryForwarder().send(params);
}

function getCommonProperties(): AnalyticsEventProperties {
  return {
    uuid: uuidv4(),
    user_id: getServerService().sessionUserId(),
    site_id: getServerService().sessionSiteLuid(),
    session_id: SessionService.getSessionId(),
    is_mobile: BrowserSupport.IsMobileBrowser()
  };
}

const getTelemetryForwarder = defaultMemoize(() => new TelemetryForwarder());

function getServerService(): SessionManagementService {
  return getSessionManagementServiceInstance();
}

export const Telemetry = {
  sendEvent,
  getCommonProperties,
  getTelemetryForwarder
};

export type Telemetry = typeof Telemetry;
